import { isArray, isEmpty, isNil, some } from 'lodash';
import {
  createPackageReservation,
  createReservation,
  createScheduledTripReservation,
} from 'redux/actions/reservation';
import { toSentence } from '.';

export const hasUnscheduledFerryTickets = () =>
  ecom.craftConfig.bookingPreferences?.unscheduledFerryTickets;

export const getHideDateOfTravel = () =>
  ecom.craftConfig.bookingPreferences?.hideDateOfTravel;

// For magnetic island and palm island, we have unscheduled ferry ticket. Its resource_class_name is not Ship but Item.
// So we use product_type_id to distinguish them.
function getUnscheduledFerryProductTypeIds() {
  const unscheduledFerryTicketTypes =
    ecom.craftConfig.bookingPreferences?.ticketTypes;
  if (!hasUnscheduledFerryTickets() || !isArray(unscheduledFerryTicketTypes)) {
    return [];
  }
  return unscheduledFerryTicketTypes.map((item) => item.productTypeId);
}

const RESERVATION_TYPES = {
  ferry: ['Ship'],
  transfer: ['ScheduledTripResource'],
  accommodation: ['Accommodation', 'TXAChannel', 'ExtraPick'],
  experience: ['Package', 'GenericResource', 'Item'],
};

const RESERVATION_TYPES_ENTRIES = Object.entries(RESERVATION_TYPES);

export const getReservationType = (reservation = {}) => {
  const unscheduledFerryProductTypeIds = getUnscheduledFerryProductTypeIds();
  /*
  In Stradbroke Island tenant, passenger ferry is a special case,
  we couldn't use resource_class_name or product_type_id to distinguish it, and
  the only way is to check if it's `product_name_underscore`'s value is `passenger_ferry`.
  We treat passenger ferry as `unscheduledFerry` since the UI and most of the logic are the same.
  */
  if (
    unscheduledFerryProductTypeIds.includes(reservation.product_type_id) ||
    reservation.product_name_underscore === 'passenger_ferry'
  ) {
    return 'unscheduledFerry';
  }

  const reservationTypeGroup = RESERVATION_TYPES_ENTRIES.find(
    ([, classNames]) => classNames.includes(reservation.resource_class_name)
  );

  if (!reservationTypeGroup) {
    return null;
  }
  return reservationTypeGroup[0];
};

// Recursively look through a reservation (and it's sub-reservations) to see if
// it contains a productType
export const reservationContainsProductType = (productTypes, reservation) => {
  const searchSpace = productTypes.split(',');
  // type productType = 'ferry'|'accommodation'|'experience'

  // Handle if this Reservation is of productType
  if (searchSpace.includes(getReservationType(reservation))) {
    return true;
  }

  if (!isArray(reservation.sub_reservations_attributes)) {
    return false;
  }

  // Check all the sub-reservation attributes
  return some(reservation.sub_reservations_attributes, (subReservatation) =>
    reservationContainsProductType(productTypes, subReservatation)
  );
};

// Pick reservations by product type from current booking
//  Note: This is a shallow look only
// productTypes = 'ferry,accommodation,experience,transfer'
export const getReservationsByProductType = (productTypes, booking) => {
  const searchSpace = productTypes.split(',');

  if (isEmpty(booking)) {
    return [];
  }
  const { reservations_attributes } = booking;
  const reservations = reservations_attributes.filter((reservation) =>
    searchSpace.includes(getReservationType(reservation))
  );

  /*
  In the Rottnest Island tenant, the optional extra reservation is very
  similar to the experience reservation, and we can't just use `resource_class_name` or
  `resource_class_name_underscore` to distinguish between the two.

  In order to determine if an reservation is an experience or an optional extra,
  we need to use the reservation's `resource_id` field and check if it's in the
  `ecom.craftConfig.bookingPreferences.optionalExtras.optionalExtras` list.
  */
  const { optionalExtras = [] } =
    ecom.craftConfig.bookingPreferences?.optionalExtras || {};
  const allOptionalExtraIds = optionalExtras.map((item) => item.qtResourceId);

  if (!searchSpace.includes('experience') || isEmpty(optionalExtras)) {
    return reservations;
  }

  return reservations.filter(
    (item) => !allOptionalExtraIds.includes(item.resource_id)
  );
};

// Pick reservations by either direct product type or recursively by product type of sub-reservations.
// e.g. does a booking contain _any_ accommodation reservations?
export const getDeepReservationsByProductType = (productTypes, booking) => {
  // type productType = 'ferry'|'accommodation'|'experience'
  if (isEmpty(booking)) {
    return [];
  }
  const { reservations_attributes } = booking;
  return reservations_attributes.filter((reservation) =>
    reservationContainsProductType(productTypes, reservation)
  );
};

// productType = 'ferry'|'accommodation'|'experience'
export const isReservationOfType = (reservation, productTypes) => {
  const searchSpace = productTypes.split(',');
  return searchSpace.includes(getReservationType(reservation));
};

// resourceClassName = 'Ship'
export const isResourceClassNameEqualTo = (reservation, resourceClassName) => {
  if (reservation.resource_class_name === resourceClassName) return true;
  return false;
};

// Are there any experiences in the booking which includes a particiular product type
// (e.g. ferry, accommodation)
export const someExperienceIncludesProductType = (productType, booking) =>
  some(getReservationsByProductType('experience', booking), (reservation) =>
    reservationContainsProductType(productType, reservation)
  );

export const getUniqueOptions = (subReservationsAttributes) => {
  const subReserervations =
    !!subReservationsAttributes &&
    subReservationsAttributes.map((sub_reservation) =>
      getReservationType(sub_reservation)
    );

  const excludeExp =
    !!subReserervations &&
    subReserervations.filter((sub) => sub !== 'experience');

  return (!!excludeExp && [...new Set(excludeExp)]) || [];
};

export const showUpSell = (reservationType, currentBooking) => {
  // Don't show up-sell if there is no booking
  if (isEmpty(currentBooking)) {
    return false;
  }

  // Don't show up-sell if there is any reservation in the booking for the type
  if (!isEmpty(getReservationsByProductType(reservationType, currentBooking))) {
    return false;
  }

  // Don't show up-sell if any experience includes the type
  if (someExperienceIncludesProductType(reservationType, currentBooking)) {
    return false;
  }

  // Show up-sell only on checkout and overview pages
  if (isEmpty(window.location.pathname.match(/bookings(\/checkout)?\/?$/))) {
    return false;
  }

  return true;
};

export const getReservationNames = (reservations) =>
  toSentence([...new Set(reservations.map((res) => res.selection_name))]);

export const reservationCreations = {
  Ship: createScheduledTripReservation,
  ScheduledTripResource: createScheduledTripReservation,
  Package: createPackageReservation,
  GenericResource: createReservation,
};

export const filterOutOptionalExtrasFromFerryReservation = ({
  reservations,
}) => {
  const { optionalExtras = [] } =
    ecom.craftConfig?.bookingPreferences?.optionalExtras || {};
  const optionalExtraRouteIds = optionalExtras
    .map((item) => item.routeIdOfCoach)
    .filter((item) => !isNil(item));

  return reservations.filter(
    (item) => !optionalExtraRouteIds.includes(item.route_id)
  );
};
